import {
  ListEntity,
  CountryEntity,
  countriesKeys,
  PaginationQuery,
} from '@seaters/constants';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { fetchCountries } from '../api';
import { useTranslation } from 'react-i18next';

export const useFetchCountries = (params: PaginationQuery) =>
  useQuery<ListEntity<CountryEntity>, Error>({
    queryKey: countriesKeys.list(params),
    queryFn: () => fetchCountries(params),
    placeholderData: keepPreviousData,
  });

// Hook that provides countries with translated names
export const useCountriesWithTranslations = (params: PaginationQuery) => {
  const { t } = useTranslation();
  const { data: countries, ...restQuery } = useFetchCountries(params);

  // Create country options with translations
  const countriesOptions = countries?.items.map((country) => {
    // Use the country name as the translation key in the 'countries' namespace
    const translatedName = t(country.name, {
      ns: 'countries',
      defaultValue: country.name, // Fallback to original name if translation missing
    });
    return {
      label: translatedName,
      value: country.alpha2Code,
    };
  });

  return {
    data: countries,
    countriesOptions,
    ...restQuery,
  };
};

// Utility function to get translated country name
export const getTranslatedCountryName = (
  countryName: string,
  t: any
): string => {
  return t(countryName, {
    ns: 'countries',
    defaultValue: countryName, // Fallback to original name if translation missing
  });
};
